<template>
  <Head
    :title="
      t('page.title', {
        institutionName: institution.name,
      })
    "
  ></Head>
  <div class="bg-gradient-to-b from-blue-50 via-transparent to-transparent">
    <div class="p-4 lg:p-8 max-w-xl mx-auto flex flex-col gap-5">
      <form
        class="flex flex-col shadow-card rounded-lg"
        @submit.prevent="
          form.post(
            route('institutions.lti-platform-registrations.store', {
              institution: institution.id,
            }),
            {
              onSuccess: sendMessageToParentIframe,
            }
          )
        "
      >
        <h1
          id="form-title"
          class="bg-gradient-to-t from-white/70 to-white/30 flex items-center justify-center p-6 rounded-t-lg border border-white/50 border-b-gray-100"
        >
          <s-logo type="image" class="logo h-16 lg:h-[4.5rem] w-auto" />
          <span class="sr-only">
            {{
              t('page.title', {
                institutionName: institution.name,
              })
            }}
          </span>
        </h1>
        <div
          class="px-5 lg:px-8 py-5 lg:py-8 bg-gradient-to-t from-gray-50 to-white rounded-b-lg flex flex-col gap-5"
        >
          <div class="bg-gray-50 p-4 rounded-lg">
            <h2 class="text-lg font-medium mb-4">{{ t('form.title') }}</h2>
            <div class="grid grid-cols-1 gap-3">
              <div class="flex flex-col">
                <span class="text-sm font-medium text-gray-700">{{ t('fields.issuer') }}</span>
                <span class="text-sm text-gray-900 break-all">
                  {{ formData.issuer }}
                </span>
              </div>

              <div class="flex flex-col">
                <span class="text-sm font-medium text-gray-700">
                  {{ t('fields.authorizationEndpoint') }}
                </span>
                <span class="text-sm text-gray-900 break-all">
                  {{ formData.authorizationEndpoint }}
                </span>
              </div>

              <div class="flex flex-col">
                <span class="text-sm font-medium text-gray-700">
                  {{ t('fields.tokenEndpoint') }}
                </span>
                <span class="text-sm text-gray-900 break-all">
                  {{ formData.tokenEndpoint }}
                </span>
              </div>

              <div class="flex flex-col">
                <span class="text-sm font-medium text-gray-700">
                  {{ t('fields.registrationEndpoint') }}
                </span>
                <span class="text-sm text-gray-900 break-all">
                  {{ formData.registrationEndpoint }}
                </span>
              </div>

              <div class="flex flex-col">
                <span class="text-sm font-medium text-gray-700">
                  {{ t('fields.jwksUrl') }}
                </span>
                <span class="text-sm text-gray-900 break-all">
                  {{ formData.jwksUrl }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex-1 flex flex-col gap-1.5"></div>

          <s-btn
            type="submit"
            color="primary"
            :processing="form.processing"
            class="mt-1"
            icon="link-plus"
            icon-end
          >
            {{ t('page.createRegistrationButton') }}
          </s-btn>
        </div>
      </form>

      <div class="flex gap-x-3 gap-y-2 flex-wrap">
        <s-link :href="route('support')" class="link-subtle flex items-center gap-1.5">
          <s-icon name="chat-question" class="opacity-70" />
          {{ t('links.help') }}
        </s-link>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {Head, useForm} from '@inertiajs/vue3';
import {useI18n} from 'vue-i18n';
import {route} from 'ziggy-js';
import SLink from '../../components/SLink.vue';
import UnauthorizedLayout from '../../layouts/UnauthorizedLayout.vue';
import SIcon from '../../design-system/SIcon.vue';
import loginTranslations from '../Login/login.json';
import SLogo from '../../design-system/SLogo.vue';
import SBtn from '../../design-system/SBtn.vue';
import SDropdownMenu from '../../design-system/SDropdownMenu.vue';
import {Institution} from '../../types/entities/institution';
import CreatePlatformRegistrationFormDto = App.DTOs.LmsIntegration.Forms.CreatePlatformRegistrationFormDto;

const props = defineProps<{
  institution: Institution;
  formData: CreatePlatformRegistrationFormDto;
}>();

const form = useForm<CreatePlatformRegistrationFormDto>(() => props.formData);

/**
 * This comes from the LTI Dynamic Registration spec:
 * https://www.imsglobal.org/spec/lti-dr/v1p0#step-4-registration-completed-and-activation
 */
const sendMessageToParentIframe = () => {
  (window.opener || window.parent).postMessage({subject: 'org.imsglobal.lti.close'}, '*');
};

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

defineOptions({layout: UnauthorizedLayout});
</script>
<i18n>
{
  "en": {
    "page.createRegistrationButton": "Create LTI Registration",
    "page.title": "{institutionName} - Create LTI Registration",
    "form.title": "LTI Registration Details",
    "fields.issuer": "Issuer",
    "fields.authorizationEndpoint": "Authorization Endpoint",
    "fields.jwksUrl": "JWKS Endpoint",
    "fields.tokenEndpoint": "Token Endpoint",
    "fields.registrationEndpoint": "Registration Endpoint"
  },
  "fr": {
    "page.createRegistrationButton": "Créer une inscription LTI",
    "page.title": "{institutionName} - Créer une inscription LTI",
    "form.title": "Détails de l'inscription LTI",
    "fields.issuer": "Émetteur",
    "fields.authorizationEndpoint": "Point de terminaison d'autorisation",
    "fields.jwksUrl": "Point de terminaison JWKS",
    "fields.tokenEndpoint": "Point de terminaison de jeton",
    "fields.registrationEndpoint": "Point de terminaison d'inscription"
  }
}
</i18n>
